import { captureException } from '@sentry/browser'
import React, { useEffect } from 'react'
import toast from 'react-hot-toast'
import {
  BrowserRouter as Router,
  Switch,
  Route,
  Redirect,
  RouteComponentProps,
} from 'react-router-dom'
import { RecoilRoot } from 'recoil'

import { routes } from 'helpers/routes'

import {
  Error403Container,
  Error404Container,
  Error500Container,
  Error502Container,
  Error503Container,
  Error504Container,
} from 'pages/error'

import LoginPage from 'pages/login'
import PdpaPage from 'pages/pdpa'

import { routeOptions } from './router'

import { TicketNotification } from 'components/Notification'
import { getSessionStorage, setSessionStorage } from 'helpers/common'
import { hasFinishedAuthentication } from 'helpers/exchangeToken'
import { fetchAPI } from 'libs/apis'
import { NotificationDetail } from 'types/types/notification'
import * as amplitude from '@amplitude/analytics-browser'

const getTicketId = async (messageId: string): Promise<string | void> => {
  if (!hasFinishedAuthentication()) {
    return
  }

  try {
    const { body } = await fetchAPI<NotificationDetail>({
      apiUrl: window._env_.REACT_APP_API_URL,
      method: 'GET',
      path: `/v1/messaging/notification/${messageId}`,
    })

    return body.dataBody.id
  } catch (error) {
    captureException(error)
  }
}

function App(): JSX.Element {
  const sw = navigator.serviceWorker
  const isAcceptConsent = getSessionStorage('consents')

  sw.onmessage = async (event) => {
    const { messageType, data, notification } = event.data

    if (messageType === 'notification-clicked') {
      const ticketId = await getTicketId(data.messageId)

      if (!ticketId) {
        return
      }

      window.location.replace(`${routes.ticket.path}/${ticketId}/detail`)
    }

    if (messageType === 'push-received') {
      const { messageId } = data as {
        messageId: string
      }
      const ticketId = await getTicketId(messageId)

      if (!ticketId) {
        return
      }

      toast({ ...notification, ticketId } as never, {
        duration: 10000,
        position: 'bottom-right',
      })
    }
  }

  useEffect(() => {
    amplitude.init('5c27d5d2dad4446e4e0aefacd7ed6885')
    Notification.requestPermission()
  }, [])

  return (
    <RecoilRoot>
      <Router>
        <TicketNotification />

        <Switch>
          <Route exact path='/login'>
            <Redirect to={'/'} />
          </Route>
          <Route exact path='/' component={LoginPage} />
          <Route exact path={routes.pdpa.path} component={PdpaPage} />

          {routeOptions?.map(({ component: Component, ...rest }, index) => {
            const routeRender = (props: RouteComponentProps) => {
              if (isAcceptConsent) {
                return <Component {...props} />
              }

              setSessionStorage(
                'pathBeforeCheckConsent',
                window.location.pathname,
              )

              return <Redirect to={'/'} />
            }

            return <Route {...rest} key={index} render={routeRender} />
          })}

          <Route path={routes.error[403]} component={Error403Container} />
          <Route path={routes.error[404]} component={Error404Container} />
          <Route path={routes.error[500]} component={Error500Container} />
          <Route path={routes.error[502]} component={Error502Container} />
          <Route path={routes.error[503]} component={Error503Container} />
          <Route path={routes.error[504]} component={Error504Container} />
          <Route component={Error404Container} />
        </Switch>
      </Router>
    </RecoilRoot>
  )
}

export default App
